import React, { useCallback, useEffect, useState } from 'react';
import './UserSettingScreen.css';
import { useAuth } from '../../context/authContext';
import { useLoading } from '../../context/loadingContext';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../context/notificationContext';
import { isSuccessCode } from '../../helpers/apiResponse';
import * as SessionAPI from '../../api/session';
import TableComponent from '../../component/form/TableComponent';
import { Column, TableButtons } from '../../types/TableComponent.types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Button } from '../../component/form/Button';
import { LoadingScreenLocal } from '../other/LoadingScreen';
import { useLanguage } from '../../context/languageContext';

type Session = {
    token: string;
    ip: string;
    is_current: string;
};

const UserSettingsScreen: React.FC = () => {
    const { association, associationRole, user, logout } = useAuth();
    const { language, setLanguage, t } = useLanguage();
    const { loading, setLoadingStatus } = useLoading();
    const { addNotification } = useNotification();
    const nav = useNavigate();

    const [sessionList, setSessionList] = useState<Session[]>([]);

    const handleApiError = useCallback((requestId: string, errorCode: string) => {
        nav(`/500?process_id=${requestId}&error_code=${errorCode}`);
    }, [nav]);

    const loadSessions = useCallback(async () => {
        const response = await SessionAPI.list();
        if (!isSuccessCode(response.error_code)) {
            return handleApiError(response.request_id, response.error_code);
        }

        const mappedSessions = (Array.isArray(response.data) ? response.data : []).map((item: Record<string, unknown>) => ({
            token: String(item.token),
            ip: String(item.ip),
            is_current: item.device_uuid === localStorage.getItem('device_uuid') ? t('settings.yes') : t('settings.no'),
        })).filter((item) => item.token !== '');
        setSessionList(mappedSessions);
    }, [handleApiError, t]);

    useEffect(() => {
        setLoadingStatus(true);
        loadSessions().finally(() => setLoadingStatus(false));
    }, [loadSessions, setLoadingStatus]);

    const onRevokeSession = async (sessionId: string) => {
        setLoadingStatus(true);
        const response = await SessionAPI.revoke(sessionId);
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }
        addNotification(t('settings.sessionRevokedTitle'), t('settings.sessionRevokedDescription'), 'success');
        await loadSessions();
        setLoadingStatus(false);
    };

    const sessionColumns: Column[] = [
        { header: t('settings.sessionId'), accessor: 'token' },
        { header: t('settings.ip'), accessor: 'ip' },
        { header: t('settings.current'), accessor: 'is_current'},
    ];
    const sessionButtons: TableButtons[] = [
        {
            button: <FontAwesomeIcon icon={faTrash} title={t('settings.revokeSession')} />,
            bc: 'red',
            placeholder: t('settings.revoke'),
            action: { param: 'token', function: onRevokeSession }
        }
    ];

    if (loading) return <LoadingScreenLocal />;

    return (
        <div className="settings-screen">
            <div className="settings-card">
                <h2>{t('settings.profile')}</h2>
                <p><strong>{t('settings.name')}:</strong> {user.name} {user.surname}</p>
                <p><strong>{t('settings.email')}:</strong> {user.email}</p>
                <p><strong>{t('settings.association')}:</strong> {association.name}</p>
                <p><strong>{t('settings.role')}:</strong> {associationRole}</p>
                <Button name={t('settings.logout')} action={logout} color="red" />
            </div>
            <div className="settings-card">
                <h2>{t('settings.sessions')}</h2>
                <TableComponent data={sessionList} columns={sessionColumns} buttons={sessionButtons} />
            </div>
            <div className="settings-card">
                <h2>{t('settings.language')}</h2>
                <p>{t('settings.languageDescription')}</p>
                <select
                    className="language-selector"
                    value={language}
                    onChange={(event) => setLanguage(event.target.value as 'it' | 'en' | 'fr' | 'de')}
                >
                    <option value="it">{t('settings.languageItalian')}</option>
                    <option value="en">{t('settings.languageEnglish')}</option>
                    <option value="fr">{t('settings.languageFrench')}</option>
                    <option value="de">{t('settings.languageGerman')}</option>
                </select>
            </div>
        </div>
    );
};

export default UserSettingsScreen;
